'use client'

import type { ExperimentClient, Variants } from '@amplitude/experiment-js-client'
import { Experiment, Source } from '@amplitude/experiment-js-client'
import { type ReactNode, createContext, useContext, useMemo } from 'react'

import { configPublic } from '@src/config.public'
import { useHasMounted } from '@src/hooks/useHasMounted'
import { log } from '@src/utils/log.util'

const { clientDeploymentKey } = configPublic.amplitude

export interface ExperimentClientWithConfig {
  experimentClient: ExperimentClient | null
  allVariants: Variants | null
}

export const ExperimentContext = createContext<ExperimentClientWithConfig>({
  experimentClient: null,
  allVariants: null,
})

export const ExperimentProvider = ({
  children,
  experiments,
}: {
  children: ReactNode
  experiments: Variants
}) => {
  const hasMounted = useHasMounted()
  const experimentClient = useMemo(() => {
    if (!hasMounted) return null

    return Experiment.initializeWithAmplitudeAnalytics(clientDeploymentKey, {
      initialVariants: experiments,
      automaticExposureTracking: false,
      source: Source.InitialVariants,
    })
  }, [experiments, hasMounted])

  return (
    <ExperimentContext.Provider value={{ experimentClient, allVariants: experiments }}>
      {children}
    </ExperimentContext.Provider>
  )
}

export function useExperimentContext() {
  const context = useContext(ExperimentContext)
  if (!context) {
    log.warn('useExperimentContext must be used within an ExperimentContext Provider')
    return null
  }
  return context
}
